import React, { useState, useEffect } from "react";
import { Text } from "@mantine/core";
import TagBadge, { tagBadgeColor, tagName } from "./TagBadge";

const manualTagOrder = [
  "like",
  "veryGood",
  "good",
  "finished",
  "bad",
  "inProgress",
  "todo",
];
const manualTagOrderIndex = manualTagOrder.reduce((acc, name, index) => {
  acc[name] = index;
  return acc;
}, {});

const SelectableTags = ({ relevantTags, alreadySelectedTags = [], onSelectionChange, language }) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [showAllTags, setShowAllTags] = useState(false);

  useEffect(() => {
    setSelectedTags(alreadySelectedTags);
  }, [alreadySelectedTags]);

  const toggleTagSelection = (selectedTag) => {
    let updatedSelection;
    if (selectedTags.find((t) => t.tag.id === selectedTag.tag.id)) {
      updatedSelection = selectedTags.filter((t) => t.tag.id !== selectedTag.tag.id);
    } else {
      updatedSelection = [...selectedTags, selectedTag];
    }
    setSelectedTags(updatedSelection);
    onSelectionChange(updatedSelection);
  };

  const alwaysVisibleTypes = new Set([2, 3, 4]);
  const selectedTagIds = new Set(selectedTags.map((tagObject) => tagObject.tag.id));
  const filteredTags = showAllTags
    ? relevantTags
    : relevantTags.filter(
        (tagObject) =>
          alwaysVisibleTypes.has(tagObject.tag.type) || selectedTagIds.has(tagObject.tag.id)
      );

  const hasAdditionalTags = relevantTags.some(
    (tagObject) => !alwaysVisibleTypes.has(tagObject.tag.type)
  );
  const orderedTags = [...filteredTags].sort((a, b) => {
    const aIsManual = a.tag.type === 3 ? 0 : 1;
    const bIsManual = b.tag.type === 3 ? 0 : 1;
    if (aIsManual !== bIsManual) return aIsManual - bIsManual;
    if (a.tag.type === 3 && b.tag.type === 3) {
      const aIndex = manualTagOrderIndex[a.tag.name] ?? 999;
      const bIndex = manualTagOrderIndex[b.tag.name] ?? 999;
      return aIndex - bIndex;
    }
    return 0; // keep backend order for non-manual tags
  });

  return (
    <div>
      <div className={`flex flex-wrap items-center gap-2`}>
        {orderedTags.map((tagObject, index) => {
          const { name } = tagObject.tag;
          const { count } = tagObject;
          const { type } = tagObject.tag;
          const isSelected = selectedTags.find((t) => t.tag.id === tagObject.tag.id);

          return (
            <TagBadge
              key={index}
              component="button"
              type="button"
              className="cursor-pointer"
              radius="md"
              size="md"
              color={tagBadgeColor(name, type)}
              variant="filled"
              style={isSelected
                ? { transform: 'scale(1.08)', boxShadow: '0 0 0 2px var(--mantine-color-body), 0 0 0 4px var(--mantine-color-text)', transition: 'transform 0.15s, box-shadow 0.15s' }
                : { transition: 'transform 0.15s' }}
              onClick={() => toggleTagSelection(tagObject)}
            >
              {tagObject.tag.imgUrl !== "" ? (
                <div className="flex items-center text-center max-h-6 gap-1">
                  <span>#</span>
                  <img className="w-5 max-w-none h-5 rounded-full" src={tagObject.tag.imgUrl} />
                  <span>{tagName(name, language)}</span>
                  <Text span size="xs" c="white" style={{ opacity: 0.85 }}>
                    ({count})
                  </Text>
                </div>
              ) : (
                <>
                  {tagName(name, language)}&nbsp;
                  <Text span size="xs" c="white" style={{ opacity: 0.85 }}>
                    ({count})
                  </Text>
                </>
              )}
            </TagBadge>
          );
        })}
        {!showAllTags && hasAdditionalTags && (
          <TagBadge
            type="button"
            component="button"
            radius="md"
            size="md"
            variant="filled"
            color="blue"
            className="cursor-pointer"
            onClick={() => setShowAllTags(true)}
            title="Show all tags"
          >
            {language === "ru" ? "Показать все" : "Show all"}
          </TagBadge>
        )}
        {showAllTags && (
          <TagBadge
            type="button"
            component="button"
            radius="md"
            size="md"
            variant="filled"
            color="blue"
            className="cursor-pointer"
            onClick={() => setShowAllTags(false)}
            title="Hide tags"
          >
            {language === "ru" ? "Скрыть" : "Hide"}
          </TagBadge>
        )}
      </div>
    </div>
  );
};

export default SelectableTags;
